import { useMemo } from 'react'
import { useQuoteContext } from './useQuoteContext'
import useFeaturePermissions from './useFeaturePermissions'

const MONTH_TO_MONTH = 1

/**
 * Calculates the number of whole months from the activation date to the
 * contract end date. Proration for the partial period between activation and
 * the next invoice date is handled separately.
 */
function calcCoTermMonths(
   startDate: string | null | undefined,
   contractTerm: number | null | undefined,
   endDate: string | null | undefined,
   activationDate: string | null | undefined,
): number | null {
   if (!startDate || !activationDate) return null
   if (!contractTerm && !endDate) return null

   const start = new Date(startDate + 'T00:00:00')
   let contractEnd: Date
   if (endDate) {
      // Salesforce EndDate is the inclusive last day; shift to exclusive end for month arithmetic
      contractEnd = new Date(endDate + 'T00:00:00')
      contractEnd.setDate(contractEnd.getDate() + 1)
   } else {
      contractEnd = new Date(start)
      contractEnd.setMonth(contractEnd.getMonth() + (contractTerm ?? 0))
   }

   const activation = new Date(activationDate + 'T00:00:00')
   if (activation >= contractEnd) return null

   let months =
      (contractEnd.getFullYear() - activation.getFullYear()) * 12 +
      (contractEnd.getMonth() - activation.getMonth())

   if (contractEnd.getDate() < activation.getDate()) {
      months--
   }

   return months > 0 ? months : null
}

/**
 * Returns the contract end date as a Date, or null.
 */
function getContractEndDate(
   startDate: string | null | undefined,
   contractTerm: number | null | undefined,
   endDate: string | null | undefined,
): Date | null {
   if (endDate) return new Date(endDate + 'T00:00:00')
   if (startDate && contractTerm) {
      const d = new Date(startDate + 'T00:00:00')
      d.setMonth(d.getMonth() + contractTerm)
      return d
   }
   return null
}

/**
 * Centralized co-term calculation shared by TermSelect and PrepaySelect.
 *
 * When a customer has an active contract, both selectors need to offer a
 * synthetic option whose value is the number of whole months remaining on
 * that contract. TermSelect uses this to render the "{n} Month Co-Term"
 * dropdown entry; PrepaySelect uses the same value to dynamically inject a
 * matching prepay option so a co-term quote can be prepaid through the end
 * of the existing contract.
 *
 * Unlike the term side (which also writes is_co_term to the opportunity when
 * the user picks the co-term option), the prepay side has NO is_co_prepay
 * column — the prepay_length number itself is the only signal stored.
 *
 * @returns coTermMonths: months from activation to contract end, or null
 * @returns hasActiveContract: true when an end date exists in the future
 * @returns activationPastContract: true when activation is past contract end
 */
export default function useCoTermMonths(): {
   coTermMonths: number | null
   hasActiveContract: boolean
   activationPastContract: boolean
   contractTerm: number | null
} {
   const state = useQuoteContext()
   const { hasPermission } = useFeaturePermissions()
   const hasExistingETUpgrades = hasPermission(
      'ExistingCustomerETUpgrades',
      state.user_role,
   )

   const latestContract = (state.accountSnowflake as any)?.latestContract
   const contractTerm: number | null = latestContract?.ContractTerm ?? null

   const coTermMonths = useMemo(
      () =>
         hasExistingETUpgrades
            ? calcCoTermMonths(
                 latestContract?.StartDate,
                 latestContract?.ContractTerm,
                 latestContract?.EndDate,
                 state.activationDate,
              )
            : null,
      [
         hasExistingETUpgrades,
         latestContract?.StartDate,
         latestContract?.ContractTerm,
         latestContract?.EndDate,
         state.activationDate,
      ],
   )

   const contractEndDate = useMemo(
      () =>
         getContractEndDate(
            latestContract?.StartDate,
            latestContract?.ContractTerm,
            latestContract?.EndDate,
         ),
      [
         latestContract?.StartDate,
         latestContract?.ContractTerm,
         latestContract?.EndDate,
      ],
   )

   const activationPastContract = useMemo(() => {
      if (!contractEndDate || !state.activationDate) return true
      const activation = new Date(state.activationDate + 'T00:00:00')
      return activation > contractEndDate
   }, [contractEndDate, state.activationDate])

   const hasActiveContract = contractEndDate != null && !activationPastContract

   return {
      coTermMonths,
      hasActiveContract,
      activationPastContract,
      contractTerm,
   }
}

export { MONTH_TO_MONTH }
